// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Glint — the pure game core. State transitions are TIMESTAMP-DRIVEN (tick(dt) advances the
// clock; tap(symbol) resolves against the current pair using the elapsed time), never
// reading a wall clock, so a bot can drive the whole thing deterministically without a
// browser and the balance sim / mechanism audit can referee it. The renderer and the
// network glue read this state; they never own the rules.
//
// A round walks a sequence of cards (deck.ts). Pair `index` shows seq[index] (top) and
// seq[index+1] (bottom); they share exactly one symbol — tap it, the bottom slides up to
// become the new top, and the next card deals in. Score rewards speed and an unbroken
// combo; a wrong tap breaks the combo (Sprint/Blaze) or ends the run (Streak).

import { buildSequence, planeSize, sharedSymbol } from './deck';
import type { Mode } from './modes';

// ── scoring constants (the mechanism audit keeps its OWN copy and asserts equality) ──
export const BASE = 100; // points for a correct tap before speed + combo
export const SPEED_MAX = 140; // max speed bonus, decaying to 0 over SPEED_WINDOW_MS
export const SPEED_WINDOW_MS = 2200; // spot within ~0.2s → ~full bonus; after 2.2s → none
export const COMBO_STEP = 0.18; // multiplier grows by this per point of combo…
export const MAX_MULT = 4; // …capped here
export const MISS_LOCK_MS = 480; // input is dead this long after a wrong tap (Sprint/Blaze)
export const BLAZE_MISS_PENALTY_MS = 2000; // a wrong tap in Blaze burns this much clock

// How many cards a non-finite mode's sequence carries, and Streak's safety cap so a
// flawless run still terminates (the balance sim requires every run to end).
export const SEQ_INFINITE = 420;
export const STREAK_CAP = 300;

export interface ResolveLog {
  pair: number; // sequence index of the top card
  shared: number; // the true unique shared symbol
  tapped: number; // the symbol the player tapped
  correct: boolean;
  atMs: number; // elapsed when tapped
  sinceCardMs: number; // time the pair had been on screen
  pts: number; // points awarded (0 on a miss)
  comboAfter: number;
}

export type GameEvent =
  | { t: 'hit'; symbol: number; combo: number; mult: number; pts: number; milestone: boolean }
  | { t: 'miss'; symbol: number }
  | { t: 'deal' }
  | { t: 'over'; won: boolean };

export interface GameState {
  mode: Mode;
  seed: number;
  seq: number[][];
  index: number; // current pair: top = seq[index], bottom = seq[index+1]
  cleared: number; // pairs cleared
  score: number;
  combo: number;
  multiplier: number;
  bestCombo: number;
  misses: number;
  elapsedMs: number;
  cardMs: number; // elapsed at which the current pair appeared
  durationMs: number; // Blaze clock length (0 otherwise)
  totalPairs: number; // Sprint target; 0 = not finite
  lockUntilMs: number; // input dead until this elapsed time
  over: boolean;
  won: boolean;
  log: ResolveLog[];
  events: GameEvent[];
}

function mult(combo: number): number {
  return Math.min(MAX_MULT, 1 + Math.max(0, combo - 1) * COMBO_STEP);
}

export function speedBonus(sinceCardMs: number): number {
  const f = Math.max(0, Math.min(1, 1 - sinceCardMs / SPEED_WINDOW_MS));
  return Math.round(SPEED_MAX * f);
}

export function createGame(mode: Mode, seed: number): GameState {
  // Finite (Sprint) → exactly one shuffle of the whole plane; endless modes → a long,
  // de-seamed sequence the clock/cap terminates.
  const minLen = mode.finite ? planeSize(mode.order) : SEQ_INFINITE;
  const seq = buildSequence(mode.order, seed >>> 0, minLen);
  const totalPairs = mode.finite ? seq.length - 1 : 0;
  return {
    mode,
    seed: seed >>> 0,
    seq,
    index: 0,
    cleared: 0,
    score: 0,
    combo: 0,
    multiplier: 1,
    bestCombo: 0,
    misses: 0,
    elapsedMs: 0,
    cardMs: 0,
    durationMs: mode.durationMs,
    totalPairs,
    lockUntilMs: 0,
    over: false,
    won: false,
    log: [],
    events: [],
  };
}

/** The current pair and its unique answer. Safe past the end (returns the last valid pair). */
export function currentPair(s: GameState): { top: number[]; bottom: number[]; shared: number } {
  const i = Math.min(s.index, s.seq.length - 2);
  const top = s.seq[i];
  const bottom = s.seq[i + 1];
  return { top, bottom, shared: sharedSymbol(top, bottom) };
}

function finish(s: GameState, won: boolean): void {
  if (s.over) return;
  s.over = true;
  s.won = won;
  s.events.push({ t: 'over', won });
}

/** Advance the clock by `dtMs`. Ends Blaze when the countdown runs out. */
export function tick(s: GameState, dtMs: number): void {
  if (s.over || dtMs <= 0) return;
  s.elapsedMs += dtMs;
  if (s.mode.clock === 'down' && s.elapsedMs >= s.durationMs) {
    s.elapsedMs = s.durationMs;
    finish(s, s.cleared > 0);
  }
}

/** Resolve a tap on `symbolId` against the current pair. */
export function tap(s: GameState, symbolId: number): void {
  if (s.over || s.elapsedMs < s.lockUntilMs) return;
  const { shared } = currentPair(s);
  const sinceCardMs = s.elapsedMs - s.cardMs;
  const correct = symbolId === shared;

  if (correct) {
    s.combo += 1;
    s.multiplier = mult(s.combo);
    const pts = Math.round((BASE + speedBonus(sinceCardMs)) * s.multiplier);
    s.score += pts;
    s.cleared += 1;
    if (s.combo > s.bestCombo) s.bestCombo = s.combo;
    s.log.push({ pair: s.index, shared, tapped: symbolId, correct: true, atMs: s.elapsedMs, sinceCardMs, pts, comboAfter: s.combo });
    const milestone = s.combo >= 5 && s.combo % 5 === 0;
    s.events.push({ t: 'hit', symbol: symbolId, combo: s.combo, mult: s.multiplier, pts, milestone });

    // advance to the next pair
    s.index += 1;
    s.cardMs = s.elapsedMs;
    if (s.mode.finite && s.cleared >= s.totalPairs) {
      finish(s, true);
      return;
    }
    if (!s.mode.finite && s.mode.sudden && s.cleared >= STREAK_CAP) {
      finish(s, true);
      return;
    }
    if (s.index + 1 >= s.seq.length) {
      // Ran off the end of the (long) sequence — treat as a clean finish.
      finish(s, true);
      return;
    }
    s.events.push({ t: 'deal' });
    return;
  }

  // a wrong tap
  s.misses += 1;
  s.log.push({ pair: s.index, shared, tapped: symbolId, correct: false, atMs: s.elapsedMs, sinceCardMs, pts: 0, comboAfter: 0 });
  s.events.push({ t: 'miss', symbol: symbolId });
  if (s.mode.sudden) {
    finish(s, false);
    return;
  }
  s.combo = 0;
  s.multiplier = 1;
  s.lockUntilMs = s.elapsedMs + MISS_LOCK_MS;
  if (s.mode.clock === 'down') {
    s.elapsedMs += BLAZE_MISS_PENALTY_MS;
    if (s.elapsedMs >= s.durationMs) {
      s.elapsedMs = s.durationMs;
      finish(s, s.cleared > 0);
    }
  }
}

/** Return and clear the queued events (juice + FX). */
export function drainEvents(s: GameState): GameEvent[] {
  const es = s.events;
  s.events = [];
  return es;
}

/** Remaining time in ms for a countdown mode (Blaze); Infinity otherwise. */
export function timeLeftMs(s: GameState): number {
  if (s.mode.clock !== 'down') return Infinity;
  return Math.max(0, s.durationMs - s.elapsedMs);
}
